import { differenceInCalendarDays } from 'date-fns';
import { listPendingOverrideRequests, requestAgeLabel } from '@/domain/approvals';
import { isConfirmationEscalated } from '@/domain/confirmationWorklist';
import { nextDueInstalment } from '@/domain/topupPlan';
import { dayKey } from '@/utils/studioDay';
import { customerName } from '@/utils/format';
import type {
  Artist,
  Booking,
  ConversationEntry,
  CustomerRecord,
  Deposit,
  NoRebookFlag,
  OverrideRequest,
  PayoutRecord,
  StudioId,
  TopupInstalment,
  TopupPlan,
} from '@/domain/types';

// ---------------------------------------------------------------------------
// Ops manager worklist (vault System Diagram 09). The accountability desk: every
// flagged item that falls out of the automated flows lands here for Ops (persona
// georgina) to action one at a time, worked to zero each day. Ops is the only
// role that can approve a no-deposit override or cancel a booking.
//
// The shell (tv-81c) fixed the section set, the section metadata, and the
// count-aggregation contract. tv-v4p replaces the empty buckets with the real
// per-feeder derivations below. The function stays PURE and deterministic
// (mirrors `buildConfirmationDay`): it reads only the read-models it is passed,
// never the store, and "now" is the sim clock the caller supplies.
// ---------------------------------------------------------------------------

/**
 * Worklist sections, in Ops-priority order: the two that need a human decision
 * first (overrides, confirmations), then the date/money items, then the
 * monitoring lists last.
 */
export const OPS_SECTION_KEYS = [
  'overrides',
  'confirmations',
  'topup-extensions',
  'payby-extensions',
  'unpaid-artists',
  'no-rebook',
  'topups',
  'unanswered',
] as const;

export type OpsSectionKey = (typeof OPS_SECTION_KEYS)[number];

export interface OpsSectionMeta {
  key: OpsSectionKey;
  /** Tab label. */
  label: string;
  /** One-line description of what lands in this section. */
  blurb: string;
  /** The diagram-09 feeder this section drains (provenance + tv-v4p wiring). */
  feeder: string;
  /**
   * True when the section cannot be fully lit until an upstream dependency
   * lands. None are pending now: the no-rebook on-the-day flag (tv-3gl) has
   * landed in main, so every section drains a real feeder.
   */
  pending?: boolean;
}

export const OPS_SECTIONS: readonly OpsSectionMeta[] = [
  {
    key: 'overrides',
    label: 'Overrides',
    blurb: 'No-deposit override requests to approve or decline. Ops only.',
    feeder: 'i2 · no-deposit override request (Agent)',
  },
  {
    key: 'confirmations',
    label: 'Confirmations to decide',
    blurb:
      'Bookings still unconfirmed after three sequences: ask the artist to try, or cancel.',
    feeder: 'i4 · confirmations 3rd-sequence flag',
  },
  {
    key: 'topup-extensions',
    label: 'Top-up extensions',
    blurb:
      'Inside-7-day top-up date-move approvals. Outside 7 days the Agent handles it directly.',
    feeder: 'i3 · inside-7d top-up extension approval (Agent)',
  },
  {
    key: 'payby-extensions',
    label: 'Pay-by extensions',
    blurb:
      'Deposit / pay-by-date extension requests. Extending re-points the reminder to the new date.',
    feeder: 'i5 · deposit / pay-by extension request',
  },
  {
    key: 'unpaid-artists',
    label: 'Unpaid artists',
    blurb: 'Arrears to chase. Ops follows up the studio manager, not the artist.',
    feeder: 'i6 · unpaid artists',
  },
  {
    key: 'no-rebook',
    label: 'No-rebook mid-piece',
    blurb:
      'Mid-piece sessions that ended with no rebook. Chase the unfinished work to a decision.',
    feeder: 'i7 · no-rebook mid-piece flag (On the day / Artist)',
  },
  {
    key: 'topups',
    label: 'Top-ups to do',
    blurb: 'Upcoming top-ups by day of week, so a date an Agent moved gets noticed.',
    feeder: 'upTU · upcoming top-ups by day of week',
  },
  {
    key: 'unanswered',
    label: 'Unanswered messages',
    blurb: 'Inbound messages left unanswered for more than 24 hours.',
    feeder: 'i1 · messages unanswered > 24h (Messenger)',
  },
];

// --- Per-section item shapes (discriminated on `kind`) ----------------------

/** i2 — a pending no-deposit override request awaiting an Ops decision. */
export interface OpsOverrideItem {
  id: string;
  kind: 'override';
  request: OverrideRequest;
  /** The held booking the request is against (absent if it has gone missing). */
  booking?: Booking;
  /** Short "how long ago" label ("3m", "2h", "1d"). */
  ageLabel: string;
}

/** i4 — a booking escalated after three unanswered confirmation sequences. */
export interface OpsConfirmationItem {
  id: string;
  kind: 'confirmation';
  booking: Booking;
  /** True once Ops has already asked the artist to personally reach out. */
  artistAsked: boolean;
}

/** i3 — an inside-7-day top-up date move that needs an Ops approval. */
export interface OpsTopupExtensionItem {
  id: string;
  kind: 'topup-extension';
  plan: TopupPlan;
  booking?: Booking;
  /** Always 'inside' here — outside-7d moves are the Agent's job, never Ops's. */
  split: 'inside';
  /** The missed instalment's pay-by date under discussion. */
  payByIso?: string;
}

/** i5 — a deposit / pay-by-date extension request (a pay-by date has arrived). */
export interface OpsPaybyExtensionItem {
  id: string;
  kind: 'payby-extension';
  plan: TopupPlan;
  deposit?: Deposit;
  /** The current pay-by date the extension would move. */
  payByIso: string;
}

/** i6 — one work day an artist is still owed (a written deferral / arrears). */
export interface OpsUnpaidArtistItem {
  id: string;
  kind: 'unpaid-artist';
  studioId: StudioId;
  artistId: string;
  artistName: string;
  amountAud: number;
  /** The work day the envelope is for. */
  dayIso: string;
}

/** i7 — a mid-piece session that ended with no rebook, flagged to Ops. */
export interface OpsNoRebookItem {
  id: string;
  kind: 'no-rebook';
  flag: NoRebookFlag;
}

/** upTU — an upcoming top-up instalment, for the by-day-of-week overview. */
export interface OpsTopupItem {
  id: string;
  kind: 'topup';
  plan: TopupPlan;
  instalment: TopupInstalment;
  dueIso: string;
  /** Studio-local day of week (0 = Sun .. 6 = Sat) of the due date. */
  dayOfWeek: number;
}

/** i1 — a customer whose last inbound message is older than 24h, unanswered. */
export interface OpsUnansweredItem {
  id: string;
  kind: 'unanswered';
  customerId: string;
  customerName: string;
  studioId: StudioId;
  lastInbound: ConversationEntry;
}

export type OpsItem =
  | OpsOverrideItem
  | OpsConfirmationItem
  | OpsTopupExtensionItem
  | OpsPaybyExtensionItem
  | OpsUnpaidArtistItem
  | OpsNoRebookItem
  | OpsTopupItem
  | OpsUnansweredItem;

export type OpsSectionBuckets = Record<OpsSectionKey, OpsItem[]>;
export type OpsSectionCounts = Record<OpsSectionKey, number> & { total: number };

export interface OpsWorklist {
  sections: OpsSectionBuckets;
  counts: OpsSectionCounts;
}

/**
 * Only the fields the unpaid-artists derivation reads. A full `Record<string,
 * Artist>` from the store is assignable, so the screen passes its store map
 * straight in while tests can supply just a display name.
 */
export type OpsArtistLite = Pick<Artist, 'id' | 'displayName'>;

/** Only the customer fields the unanswered derivation reads (store map assignable). */
export type OpsCustomerLite = Pick<
  CustomerRecord,
  'id' | 'firstName' | 'lastName' | 'preferredName' | 'primaryStudioId' | 'conversation'
>;

export interface BuildOpsWorklistArgs {
  /** The workday to build the list for; "now" per the sim clock. */
  nowIso: string;
  /** Studio scope; [] = all studios. Every feeder filters to these. */
  studioIds: StudioId[];
  /** Read-models the feeders drain. Absent === none of that kind, so the desk is empty. */
  bookings?: Record<string, Booking>;
  overrideRequests?: Record<string, OverrideRequest>;
  topupPlans?: Record<string, TopupPlan>;
  deposits?: Record<string, Deposit>;
  noRebookFlags?: Record<string, NoRebookFlag>;
  payoutRecords?: Record<string, PayoutRecord>;
  artists?: Record<string, OpsArtistLite>;
  customers?: Record<string, OpsCustomerLite>;
}

function countBuckets(sections: OpsSectionBuckets): OpsSectionCounts {
  const perSection = Object.fromEntries(
    OPS_SECTION_KEYS.map((key) => [key, sections[key].length]),
  ) as Record<OpsSectionKey, number>;
  const total = OPS_SECTION_KEYS.reduce((sum, key) => sum + sections[key].length, 0);
  return { ...perSection, total };
}

const DAY_MS = 24 * 3_600_000;

/**
 * Build the Ops worklist for a day + studio scope from the passed-in read-models.
 * Pure + deterministic: no store reads, no `Date.now()`. Each feeder maps onto its
 * section per the runbook's diagram-09 feeder->signal map.
 */
export function buildOpsWorklist(args: BuildOpsWorklistArgs): OpsWorklist {
  const {
    nowIso,
    studioIds,
    bookings = {},
    overrideRequests = {},
    topupPlans = {},
    deposits = {},
    noRebookFlags = {},
    payoutRecords = {},
    artists = {},
    customers = {},
  } = args;
  const nowMs = new Date(nowIso).getTime();
  const inScope = (studioId: StudioId): boolean =>
    studioIds.length === 0 || studioIds.includes(studioId);

  // i2 · overrides — pending no-deposit requests, oldest first, with their booking.
  // An OverrideRequest carries no studioId of its own; the studio lives on the
  // referenced booking. When that booking is absent from the read-model (the seed
  // evicts the held Coburg demo-day approval bookings while leaving their pending
  // requests intact) the studio is unknowable, so we CANNOT prove the request is
  // out of scope. Surface it anyway rather than silently dropping a real pending
  // decision — this matches /approvals, which lists every pending request. A
  // present booking is still scoped as before.
  const overrides: OpsItem[] = listPendingOverrideRequests(overrideRequests)
    .map((request) => ({ request, booking: bookings[request.bookingId] }))
    .filter(
      ({ booking }) =>
        studioIds.length === 0 || !booking || studioIds.includes(booking.studioId),
    )
    .map(
      ({ request, booking }): OpsOverrideItem => ({
        id: request.id,
        kind: 'override',
        request,
        booking,
        ageLabel: requestAgeLabel(request.createdAt, nowMs),
      }),
    );

  // i4 · confirmations — escalated (3 unanswered sequences, still unconfirmed).
  const confirmations: OpsItem[] = Object.values(bookings)
    .filter((b) => isConfirmationEscalated(b) && inScope(b.studioId))
    .sort((a, b) => a.start.localeCompare(b.start))
    .map(
      (booking): OpsConfirmationItem => ({
        id: booking.id,
        kind: 'confirmation',
        booking,
        artistAsked: !!booking.artistAskedAt,
      }),
    );

  // i3 · topup-extensions — active plans with a missed instalment INSIDE 7 days
  // of their appointment (the needs-ops-approval branch). Outside 7 days is the
  // Agent's job and never appears here.
  const topupExtensions: OpsItem[] = Object.values(topupPlans)
    .filter((p) => p.status === 'active' && inScope(p.studioId))
    .filter((p) => p.instalments.some((i) => i.status === 'missed'))
    .map((plan) => ({ plan, booking: plan.bookingId ? bookings[plan.bookingId] : undefined }))
    .filter(
      (x): x is { plan: TopupPlan; booking: Booking } =>
        x.booking !== undefined &&
        differenceInCalendarDays(new Date(x.booking.start), new Date(nowIso)) < 7,
    )
    .sort((a, b) => a.booking.start.localeCompare(b.booking.start))
    .map(
      ({ plan, booking }): OpsTopupExtensionItem => ({
        id: plan.id,
        kind: 'topup-extension',
        plan,
        booking,
        split: 'inside',
        payByIso: plan.instalments.find((i) => i.status === 'missed')?.dueIso,
      }),
    );

  // i5 · payby-extensions — active plans whose current instalment pay-by date has
  // arrived (scheduled + due), where the client may ask to push the date.
  const paybyExtensions: OpsItem[] = Object.values(topupPlans)
    .filter((p) => p.status === 'active' && inScope(p.studioId))
    .map((plan) => ({ plan, due: nextDueInstalment(plan, nowIso) }))
    .filter((x): x is { plan: TopupPlan; due: TopupInstalment } => x.due !== null)
    .sort((a, b) => a.due.dueIso.localeCompare(b.due.dueIso))
    .map(
      ({ plan, due }): OpsPaybyExtensionItem => ({
        id: plan.id,
        kind: 'payby-extension',
        plan,
        deposit: deposits[plan.depositId],
        payByIso: due.dueIso,
      }),
    );

  // i6 · unpaid-artists — written arrears (deferred payout records), oldest first.
  const unpaidArtists: OpsItem[] = Object.values(payoutRecords)
    .filter((r) => r.status === 'deferred' && inScope(r.studioId))
    .sort((a, b) => a.dayIso.localeCompare(b.dayIso))
    .map(
      (r): OpsUnpaidArtistItem => ({
        id: r.id,
        kind: 'unpaid-artist',
        studioId: r.studioId,
        artistId: r.artistId,
        artistName: artists[r.artistId]?.displayName ?? r.artistId,
        amountAud: r.amountDueAud,
        dayIso: r.dayIso,
      }),
    );

  // i7 · no-rebook — unresolved mid-piece flags (tv-3gl landed; now a live feeder).
  const noRebook: OpsItem[] = Object.values(noRebookFlags)
    .filter((f) => !f.resolvedAt && inScope(f.studioId))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .map((flag): OpsNoRebookItem => ({ id: flag.id, kind: 'no-rebook', flag }));

  // upTU · topups — the forward overview: each active plan's next FUTURE scheduled
  // instalment, so a date an Agent moved gets noticed under Ops.
  const topups: OpsItem[] = Object.values(topupPlans)
    .filter((p) => p.status === 'active' && inScope(p.studioId))
    .map((plan) => {
      const next = plan.instalments
        .filter((i) => i.status === 'scheduled' && new Date(i.dueIso).getTime() > nowMs)
        .sort((a, b) => a.dueIso.localeCompare(b.dueIso))[0];
      return next ? { plan, next } : undefined;
    })
    .filter((x): x is { plan: TopupPlan; next: TopupInstalment } => x !== undefined)
    .sort((a, b) => a.next.dueIso.localeCompare(b.next.dueIso))
    .map(
      ({ plan, next }): OpsTopupItem => ({
        id: plan.id,
        kind: 'topup',
        plan,
        instalment: next,
        dueIso: next.dueIso,
        dayOfWeek: new Date(`${dayKey(next.dueIso)}T12:00:00+10:00`).getDay(),
      }),
    );

  // i1 · unanswered — customers whose LAST message is inbound and older than 24h
  // (no outbound since, or the last entry would not be inbound).
  const unanswered: OpsItem[] = Object.values(customers)
    .filter((c) => inScope(c.primaryStudioId))
    .map((c) => ({ c, last: c.conversation.at(-1) }))
    .filter(
      (x): x is { c: OpsCustomerLite; last: ConversationEntry } =>
        x.last !== undefined &&
        x.last.direction === 'in' &&
        nowMs - new Date(x.last.timestamp).getTime() > DAY_MS,
    )
    .sort((a, b) => a.last.timestamp.localeCompare(b.last.timestamp))
    .map(
      ({ c, last }): OpsUnansweredItem => ({
        id: c.id,
        kind: 'unanswered',
        customerId: c.id,
        customerName: customerName(c),
        studioId: c.primaryStudioId,
        lastInbound: last,
      }),
    );

  const sections: OpsSectionBuckets = {
    overrides,
    confirmations,
    'topup-extensions': topupExtensions,
    'payby-extensions': paybyExtensions,
    'unpaid-artists': unpaidArtists,
    'no-rebook': noRebook,
    topups,
    unanswered,
  };
  return { sections, counts: countBuckets(sections) };
}
